import type { Transformation, TransformationFormState } from "@/clinical-codes-transformations/types";
import type { CodeType } from "@/clinical-codes/types/ClinicalCodeTypes";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { PlusIcon } from "lucide-react";
import { useEffect, useState } from "react";
import { CodeSelector } from "./CodeSelector";
import { ConditionSetItem } from "./ConditionSetItem";

interface TransformationFormProps {
  initialData?: Transformation;
  onSubmit: (data: TransformationFormState) => void;
  onCancel: () => void;
  isSubmitting?: boolean;
}

function transformationToFormState(transformation: Transformation): TransformationFormState {
  return {
    id: transformation.id,
    code_type: transformation.code_type,
    code_id: transformation.code_id,
    condition_sets: [
      ...transformation.condition_sets.map((set) => ({
        id: set.id,
        conditions: [
          ...set.conditions.map((cond) => ({
            id: cond.id,
            field_id: cond.field_id,
            datapoint_id: cond.datapoint_id,
            operator: cond.operator,
          })),
        ],
      })),
    ],
  };
}

const initialFormData: TransformationFormState = {
  id: "",
  code_type: "ICD-10",
  code_id: "",
  condition_sets: [
    {
      conditions: [
        {
          field_id: "",
          datapoint_id: "",
          operator: "AND",
        },
      ],
    },
  ],
};

export const TransformationForm = ({
  initialData,
  onCancel,
  onSubmit,
  isSubmitting = false,
}: TransformationFormProps) => {
  const [formData, setFormData] = useState<TransformationFormState>(initialFormData);

  useEffect(() => {
    if (initialData) {
      setFormData(transformationToFormState(initialData));
    }
  }, [initialData]);

  const handleCancel = () => {
    setFormData(initialFormData);
    onCancel();
  };

  const handleCodeSelect = (codeId: string, codeType: CodeType) => {
    setFormData((prev) => ({
      ...prev,
      code_id: codeId,
      code_type: codeType,
    }));
  };

  const handleAddConditionSet = () => {
    setFormData((prev) => ({
      ...prev,
      condition_sets: [
        ...prev.condition_sets,
        {
          conditions: [
            {
              field_id: "",
              datapoint_id: "",
              operator: "AND",
            },
          ],
        },
      ],
    }));
  };

  const handleRemoveConditionSet = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      condition_sets: prev.condition_sets.filter((_, i) => i !== index),
    }));
  };

  const handleUpdateConditionSet = (
    index: number,
    conditions: Array<{
      id?: string;
      field_id: string;
      datapoint_id: string;
      operator: "AND" | "OR" | "ONLY" | "NOT";
    }>,
  ) => {
    setFormData((prev) => {
      const newConditionSets = [...prev.condition_sets];
      newConditionSets[index] = {
        ...newConditionSets[index],
        conditions,
      };
      return {
        ...prev,
        condition_sets: newConditionSets,
      };
    });
  };

  const handleSubmit = () => {
    onSubmit(formData);
  };

  const isValid = () => {
    return (
      formData.code_id !== "" &&
      formData.condition_sets.length > 0 &&
      formData.condition_sets.every(
        (set) =>
          set.conditions.length > 0 &&
          set.conditions.every((condition) => condition.field_id !== "" && condition.datapoint_id !== ""),
      )
    );
  };

  return (
    <Card>
      <CardContent className="pt-6 max-h-[70vh] overflow-y-auto">
        <div className="space-y-6">
          {/* Header with save/delete buttons */}
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-medium">{initialData ? "Edit Transformation" : "Create Transformation"}</h3>
            {/* Submit button */}
            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" onClick={handleSubmit} disabled={!isValid() || isSubmitting}>
                {isSubmitting ? <span>Saving...</span> : <span>Save Transformation</span>}
              </Button>
              <Button variant="outline" size="sm" type="button" onClick={handleCancel}>
                Cancel
              </Button>
            </div>
          </div>

          {/* Code selection */}
          <div className="space-y-2">
            <h3 className="text-sm font-medium">1. Select Clinical Code</h3>
            <CodeSelector
              selectedCodeId={formData.code_id}
              selectedCodeType={formData.code_type}
              onCodeSelect={handleCodeSelect}
            />
          </div>

          {/* Condition sets */}
          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <h3 className="text-sm font-medium">2. Define Conditions</h3>
              <Button variant="outline" size="sm" type="button" onClick={handleAddConditionSet}>
                <PlusIcon className="h-4 w-4 mr-2" />
                Add Condition Set
              </Button>
            </div>

            <ScrollArea className="pr-4">
              <div className="space-y-4 pr-2">
                {formData.condition_sets.map((conditionSet, index) => {
                  return (
                    <ConditionSetItem
                      key={index}
                      id={conditionSet.id}
                      conditions={conditionSet.conditions}
                      onDelete={() => handleRemoveConditionSet(index)}
                      onChange={(conditions) => handleUpdateConditionSet(index, conditions)}
                    />
                  );
                })}
              </div>
            </ScrollArea>

            {formData.condition_sets.length === 0 && (
              <div className="text-sm text-muted-foreground text-center p-4 border rounded-md">
                No condition sets added. Click "Add Condition Set" to get started.
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
